// ─────────────────────────────────────────────────────────────────
// Glass-i — Accounts page-state helpers (Stage 6B.9F — Hardening).
//
// Pure-JS helpers (no JSX, no React) for resilient loading/error/empty
// rendering on the accounting report + setup pages. The JSX components
// import `pickAccountsPageState` + `friendlyAccountsErrorMessage` to
// decide what to render based on the async lifecycle of their data
// fetch.
//
// API
// ─────────────────────────────────────────────────────────────────
//   pickAccountsPageState({ loading, error, data, isEmpty })
//     → 'loading' | 'error' | 'empty' | 'success'
//
//   friendlyAccountsErrorMessage(err, context)
//     → string  — friendly message tuned for the given page context
//                 (e.g. "Could not load trial balance...").
//
//   ACCOUNTS_EMPTY_MESSAGES — page-context → "no rows" copy.
//   ACCOUNTS_ERROR_FALLBACKS — page-context → generic fallback copy.
// ─────────────────────────────────────────────────────────────────

export const ACCOUNTS_EMPTY_MESSAGES = Object.freeze({
  trial_balance:    'No ledger entries match the selected filters.',
  ledger_statement: 'No ledger entries found for the selected period.',
  voucher_register: 'No vouchers found for the selected filters.',
  account_balances: 'No ledgers match the selected filters.',
  profit_loss:      'No revenue / expense activity in the selected period.',
  balance_sheet:    'No ledger balances available for the selected as-of date.',
  // Stage 6B.9H — schedule report contexts (already used by the
  // 6B.9G Fixed Asset / Investment Schedule pages).
  fixed_asset_schedule: 'No fixed assets match the current filters.',
  investment_schedule:  'No investments match the current filters.',
  // Stage 6E.1 — Bank Reconciliation + Cheque Tracking contexts.
  bank_reconciliation:        'No bank reconciliations yet. Create one for the current statement period.',
  bank_reconciliation_lines:  'No bank statement lines added yet. Add lines from your bank statement to begin matching.',
  bank_reconciliation_book:   'No posted bank-ledger entries in this period.',
  cheque_register:            'No cheque-mode payments found.',
  cheque_events:              'No cheque events recorded yet.',
  // Stage 6E.2 — Year-End Closing + Opening Balance Voucher.
  year_end_closing:           'No year-end closings yet. Create one for the financial year you want to finalise.',
  year_end_closing_lines:     'No closing lines yet. Generate a preview to populate the snapshot.',
  // Stage 6E.3 — Debtors / Creditors Aging.
  debtors_aging:              'No outstanding receivables found.',
  creditors_aging:            'No outstanding payables found.',
  aging_detail:               'No open vouchers found for this party / ledger.',
  periods:          'No accounting periods configured. Generate FY periods in Accounts Setup.',
  setup:            'Accounting setup is incomplete.',
  document_sequences: 'No document sequences configured for this tenant.',
  opening_balances:   'No opening balances captured yet. Edit a row to record one.',
  audit_log:        'No audit entries for the selected entity.',
  ledgers:          'No ledger accounts match the current filters.',
  tax_rates:        'No tax rates configured.',
  default:          'No records found.'
});

export const ACCOUNTS_ERROR_FALLBACKS = Object.freeze({
  trial_balance:    'Could not load the Trial Balance. Check the filters and try again.',
  ledger_statement: 'Could not load the Ledger Statement. Check the date range and try again.',
  voucher_register: 'Could not load the Voucher Register. Check the filters and try again.',
  account_balances: 'Could not load Account Balances. Please try again.',
  profit_loss:      'Could not load the Profit & Loss report. Check the date range and try again.',
  balance_sheet:    'Could not load the Balance Sheet. Check the as-of date and try again.',
  // Stage 6B.9H — schedule report contexts.
  fixed_asset_schedule: 'Could not load the Fixed Asset Schedule. Check the filters and try again.',
  investment_schedule:  'Could not load the Investment Schedule. Check the filters and try again.',
  // Stage 6E.1 — Bank Reconciliation + Cheque Tracking contexts.
  bank_reconciliation:       'Could not load bank reconciliations. Please try again.',
  bank_reconciliation_lines: 'Could not load bank statement lines. Please try again.',
  bank_reconciliation_book:  'Could not load book-side entries. Please try again.',
  cheque_register:           'Could not load the cheque register. Please try again.',
  cheque_events:             'Could not load cheque events. Please try again.',
  // Stage 6E.2 — Year-End Closing.
  year_end_closing:          'Could not load year-end closings. Please try again.',
  year_end_closing_lines:    'Could not load the closing snapshot lines. Please try again.',
  // Stage 6E.3 — Debtors / Creditors Aging.
  debtors_aging:             'Could not load the Debtors Aging report. Check the filters and try again.',
  creditors_aging:           'Could not load the Creditors Aging report. Check the filters and try again.',
  aging_detail:              'Could not load the party aging detail. Please try again.',
  periods:          'Could not load accounting periods. Please try again.',
  setup:            'Could not load the Accounts Setup readiness. Please try again.',
  document_sequences: 'Could not load document sequences. Please try again.',
  opening_balances:   'Could not load opening balances. Please try again.',
  audit_log:        'Could not load the audit log. Please try again.',
  ledgers:          'Could not load ledger accounts. Please try again.',
  tax_rates:        'Could not load tax rates. Please try again.',
  default:          'Something went wrong. Please try again.'
});

// Standard async-state → render-mode picker. The component reads the
// returned string and switches between four jsx blocks.
//
// `isEmpty` defaults to `data == null || data.rows?.length === 0` so
// the caller usually does not need to pass it — they only override
// when the data shape is unusual (e.g. balanceSheet returns a tree).
export function pickAccountsPageState({ loading, error, data, isEmpty }) {
  if (loading) return 'loading';
  if (error)   return 'error';
  let empty;
  if (typeof isEmpty === 'boolean') {
    empty = isEmpty;
  } else if (data == null) {
    empty = true;
  } else if (Array.isArray(data)) {
    empty = data.length === 0;
  } else if (Array.isArray(data.rows)) {
    empty = data.rows.length === 0;
  } else {
    empty = false;
  }
  return empty ? 'empty' : 'success';
}

// Translate any caught error into a friendly user-facing string. The
// priority order is:
//   1. Joi / backend `error.response.data.error.message`
//   2. Axios `error.response.statusText`
//   3. JS `error.message`
//   4. Per-context default fallback
//
// Stage 6B.9F: also translates a handful of well-known accounting
// error codes that occasionally leak past the backend's user-facing
// message ("VOUCHER_PERIOD_LOCKED", "ACCOUNTING_CHECK_FAILED" etc.).
const ACCOUNTING_CODE_OVERRIDES = Object.freeze({
  VOUCHER_PERIOD_LOCKED:  'This accounting period is locked. You cannot post vouchers in this period.',
  VOUCHER_PERIOD_CLOSED:  'This accounting period is closed. You cannot post vouchers in this period.',
  VOUCHER_PERIOD_MISSING: 'No accounting period is configured for this voucher date. Generate FY periods in Accounts Setup.',
  VOUCHER_NOT_BALANCED:   'Voucher lines must be valid and balanced before posting (total debit = total credit).',
  VOUCHER_LINE_INVALID:   'Each voucher line must have either Debit or Credit, not both, and the amount must be greater than zero.',
  VOUCHER_NO_DUPLICATE:   'Voucher number already exists. Please refresh — the document sequence will pick the next free number.',
  PAYMENT_NO_DUPLICATE:   'Payment / receipt number already exists. Please refresh and retry.',
  PAYMENT_CHEQUE_REQUIRED:'Cheque number and cheque status are required when posting a cheque payment.',
  CONTRA_LEDGER_NOT_CASH_BANK: 'Contra voucher can be posted only between Cash and Bank ledgers.',
  ACCOUNTING_REFERENCE_MISSING: 'The selected ledger or party does not exist or has been deleted. Pick a current option.',
  ACCOUNTING_DUPLICATE_KEY:     'A duplicate record was detected. Please refresh and retry.',
  ACCOUNTING_REQUIRED_MISSING:  'A required field is missing. Please complete all required fields and retry.',
  ACCOUNTING_INVALID_VALUE:     'One of the values is in the wrong format. Please review the form and retry.',
  ACCOUNTING_CHECK_FAILED:      'A validation rule on the record was violated. Please review the form and retry.',
  ACCOUNTING_RACE_LOST:         'The record changed concurrently. Please refresh and try again.',
  // Stage 6B.9H — keep wording consistent across Fixed Assets +
  // Investments error codes too, even though their service layers
  // already produce friendly messages today.
  FIXED_ASSET_OVER_DEPRECIATED: 'Cannot post — depreciation would exceed the asset cost. Review the schedule and remaining WDV.',
  FIXED_ASSET_ALREADY_DISPOSED: 'This asset is already disposed. Cancel the existing disposal first.',
  INVESTMENT_ALREADY_DISPOSED:  'This investment is already disposed or matured.',
  INVESTMENT_INVALID_STATE:     'This action is not allowed in the investment\'s current status.',
  // Stage 6E.1 — Bank Reconciliation + Cheque overrides.
  BANK_LEDGER_INVALID:          'Bank reconciliation can only run against a bank ledger.',
  BANK_LEDGER_INACTIVE:         'Selected bank account is inactive. Pick an active ledger.',
  BANK_RECON_NOT_EDITABLE:      'This reconciliation cannot be edited in its current status.',
  BANK_RECON_LOCKED:            'Unlock the reconciliation before changing matches.',
  BANK_RECON_NOT_DELETABLE:     'A finalised reconciliation cannot be deleted — cancel it instead.',
  STMT_LINE_ALREADY_MATCHED:    'This statement line is already matched. Unmatch first.',
  STMT_LINE_MATCHED:            'Unmatch the line before editing or deleting it.',
  VOUCHER_LINE_ALREADY_MATCHED: 'This voucher line is already matched to another statement line.',
  VOUCHER_LINE_WRONG_LEDGER:    'The selected voucher line is not on this bank ledger.',
  VOUCHER_NOT_POSTED:           'Only posted, non-deleted voucher lines can be matched.',
  MATCH_AMOUNT_EXCEEDS_LINE:    'Matched amount cannot exceed the statement line amount.',
  CHEQUE_TRANSITION_INVALID:    'This cheque status change is not allowed from the current status.',
  PAYMENT_NOT_CHEQUE:           'Cheque workflow applies only to cheque-mode payments.',
  // Stage 6E.2 — Year-End Closing.
  YEC_RE_LEDGER_REQUIRED:       'Retained earnings / capital ledger is required.',
  YEC_RE_LEDGER_NOT_EQUITY:     'Retained earnings account must be an active Equity ledger.',
  YEC_RE_LEDGER_INACTIVE:       'Selected retained earnings ledger is inactive. Pick an active equity ledger.',
  YEC_RE_LEDGER_INVALID_SUBTYPE:'Retained earnings ledger should be classified as Capital or Reserves & Surplus.',
  YEC_ACTIVE_FY_DUPLICATE:      'Another active year-end closing exists for the same financial year window. Cancel or delete it first.',
  YEC_PREVIEW_REQUIRED:         'Generate a preview before posting the year-end closing.',
  YEC_NOT_BALANCED:             'The closing voucher is not balanced. Re-run the preview before posting.',
  YEC_NO_PL_ACTIVITY:           'No income / expense activity to close in this period. Add transactions or cancel the closing.',
  YEC_ALREADY_CLOSED:           'This year-end closing is already closed.',
  YEC_NOT_EDITABLE:             'This year-end closing cannot be edited in its current status.',
  YEC_NOT_CLOSED:               'Create the year-end closing before creating opening balance voucher.',
  OBV_DUPLICATE:                'Opening balance voucher already exists for this closing.',
  YEC_REOPEN_NOT_IMPLEMENTED:   'Reopen workflow is not implemented in Stage 6E.2. Cancel the closing manually and recreate it for the same FY window.',
  // Stage 6E.3 — Debtors / Creditors Aging.
  AGING_REPORTS_VALIDATION_FAILED: 'One of the report filters is invalid. Please review and try again.',
  AGING_REPORT_TYPE_MISSING:       'Report type is required (debtors or creditors).',
  AGING_TARGET_NOT_FOUND:          'No matching customer / supplier ledger found for this aging drill-down.'
});

export function friendlyAccountsErrorMessage(err, context) {
  const ctx = context && ACCOUNTS_ERROR_FALLBACKS[context]
    ? context : 'default';
  const fallback = ACCOUNTS_ERROR_FALLBACKS[ctx];
  if (!err) return fallback;
  const body = err.response && err.response.data && err.response.data.error
    ? err.response.data.error : null;
  if (body) {
    if (body.code && ACCOUNTING_CODE_OVERRIDES[body.code]) {
      return ACCOUNTING_CODE_OVERRIDES[body.code];
    }
    if (body.message) return body.message;
  }
  if (err.message && typeof err.message === 'string') return err.message;
  return fallback;
}

// Convenience: pick the "no data" copy for the same context.
export function friendlyAccountsEmptyMessage(context) {
  return ACCOUNTS_EMPTY_MESSAGES[context]
    || ACCOUNTS_EMPTY_MESSAGES.default;
}
